<template>
  <section
    v-if="canComment"
    data-testid="comments-panel"
    :class="
      compact
        ? 'relative flex max-h-[28rem] flex-col overflow-hidden rounded-md border border-obsidian-200 bg-white'
        : ''
    "
  >
    <div
      v-if="compact && isFetching && !threads?.length"
      class="absolute inset-x-0 top-0 h-0.5 overflow-hidden"
    >
      <div class="loading-sweep h-full w-1/4 bg-obsidian-600"></div>
    </div>

    <header
      v-if="(!compact && !hideTitle) || resolvedCount"
      class="flex items-center justify-between gap-3"
      :class="compact ? 'shrink-0 border-b border-obsidian-100 px-3 py-2' : 'pb-2'"
    >
      <h2
        v-if="!compact && !hideTitle"
        class="flex items-center gap-2 text-sm font-semibold text-obsidian-900"
      >
        <MessageSquareTextIcon class="size-4 shrink-0 text-obsidian-400" />
        {{ t('title') }}
        <span
          v-if="openCount"
          class="rounded-sm bg-obsidian-100 px-1.5 text-xs font-medium tabular-nums text-obsidian-600"
        >
          {{ openCount }}
        </span>
      </h2>
      <button
        v-if="resolvedCount"
        type="button"
        class="ml-auto shrink-0 rounded-sm px-1.5 py-0.5 text-xs font-medium text-obsidian-500 transition hover:bg-hazel-100 hover:text-obsidian-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        @click="showResolved = !showResolved"
      >
        {{ showResolved ? t('hideResolved') : t('showResolved', { count: resolvedCount }) }}
      </button>
    </header>

    <div
      v-if="!compact || visibleThreads.length"
      :class="
        compact ? 'min-h-0 flex-1 divide-y divide-obsidian-100 overflow-y-auto px-3' : 'space-y-2'
      "
    >
      <div v-if="isFetching && !threads?.length" class="flex justify-center py-6">
        <Spinner class="size-5" />
      </div>

      <p v-else-if="isError" class="py-1 text-xs text-obsidian-500">
        {{ t('loadError') }}
      </p>

      <p v-else-if="!visibleThreads.length" class="py-1 text-xs text-obsidian-500">
        {{ t('empty') }}
      </p>

      <TransitionGroup name="thread">
        <article
          v-for="thread in visibleThreads"
          :key="thread.id"
          class="group/thread transition-colors"
          :class="
            compact
              ? 'py-3'
              : [
                  'rounded-md border p-3',
                  thread.resolvedAt
                    ? 'border-obsidian-200 bg-obsidian-50'
                    : 'border-obsidian-200 bg-white',
                ]
          "
        >
          <AnimatedHeightTransition :duration-ms="ANIMATION_MS">
            <div>
              <p
                v-if="thread.resolvedAt"
                class="mb-2 flex items-center gap-1 text-xs font-medium text-obsidian-500"
              >
                <CircleCheckIcon class="size-3.5 shrink-0" />
                <span class="truncate">{{ resolvedLabel(thread) }}</span>
              </p>

              <div :class="{ 'opacity-75': thread.resolvedAt }">
                <CommentItem
                  :comment="thread"
                  :connected="compact && thread.replies.length > 0"
                  :entity-type="entityType"
                  :entity-id="entityId"
                  :me-id="me?.id"
                  :can-edit="thread.author.id === me?.id"
                  :placeholder="t('placeholder')"
                  :no-matches="t('noMatches')"
                  :just-now-label="t('justNow')"
                  :save-label="t('save')"
                  :cancel-label="t('cancel')"
                  :edit-label="t('edit')"
                  :delete-label="t('delete')"
                  :edited-label="t('edited')"
                  @save="handleEdit"
                  @delete="handleDelete"
                  @react="handleReact"
                >
                  <template #actions>
                    <CVButton
                      type="button"
                      variant="ghost"
                      size="iconXs"
                      :class="[
                        'hover:text-obsidian-800 focus-visible:opacity-100',
                        thread.resolvedAt
                          ? 'text-obsidian-500'
                          : 'text-obsidian-400 opacity-0 group-hover/thread:opacity-100',
                      ]"
                      :aria-label="thread.resolvedAt ? t('unresolve') : t('resolve')"
                      :title="thread.resolvedAt ? t('unresolve') : t('resolve')"
                      @click="handleResolve(thread)"
                    >
                      <component
                        :is="thread.resolvedAt ? RotateCcwIcon : CheckIcon"
                        class="size-4"
                      />
                    </CVButton>
                  </template>
                </CommentItem>
              </div>

              <div
                v-if="thread.replies.length"
                class="mt-3 space-y-3"
                :class="[
                  compact ? '' : 'border-t border-obsidian-100 pt-3',
                  { 'opacity-75': thread.resolvedAt },
                ]"
              >
                <CommentItem
                  v-for="(reply, index) in thread.replies"
                  :key="reply.id"
                  :comment="reply"
                  :connected="compact && index < thread.replies.length - 1"
                  :entity-type="entityType"
                  :entity-id="entityId"
                  :me-id="me?.id"
                  :can-edit="reply.author.id === me?.id"
                  :placeholder="t('placeholder')"
                  :no-matches="t('noMatches')"
                  :just-now-label="t('justNow')"
                  :save-label="t('save')"
                  :cancel-label="t('cancel')"
                  :edit-label="t('edit')"
                  :delete-label="t('delete')"
                  :edited-label="t('edited')"
                  @save="handleEdit"
                  @delete="handleDelete"
                  @react="handleReact"
                />
              </div>

              <template v-if="!thread.resolvedAt">
                <div v-if="replyingToId !== thread.id" class="mt-2 flex gap-2.5">
                  <div class="w-7 shrink-0" aria-hidden="true"></div>
                  <button
                    type="button"
                    class="-ml-1 flex items-center gap-1.5 rounded-sm px-1 py-0.5 text-xs font-medium text-obsidian-500 transition hover:bg-hazel-100 hover:text-obsidian-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    @click="replyingToId = thread.id"
                  >
                    <ReplyIcon class="size-3.5" />
                    {{ t('reply') }}
                  </button>
                </div>
                <div v-else class="mt-3 flex items-start gap-2.5">
                  <CommentAvatar
                    :user-id="me?.id"
                    :first-name="me?.firstName"
                    :last-name="me?.lastName"
                  />
                  <div class="min-w-0 flex-1 space-y-1">
                    <CommentEditor
                      ref="replyEditor"
                      bare
                      autofocus
                      :entity-type="entityType"
                      :entity-id="entityId"
                      :placeholder="t('replyPlaceholder')"
                      :no-matches="t('noMatches')"
                      :show-submit="true"
                      :is-submitting="isCreating"
                      :submit-label="t('reply')"
                      @submit="handleSubmit(thread.id)"
                    />
                    <button
                      type="button"
                      class="-ml-1 rounded-sm px-1 text-xs text-obsidian-500 transition hover:text-obsidian-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      @click="replyingToId = null"
                    >
                      {{ t('cancel') }}
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </AnimatedHeightTransition>
        </article>
      </TransitionGroup>
    </div>

    <div
      data-testid="comment-composer"
      class="flex items-start gap-2.5 border-obsidian-100 bg-white p-3 transition"
      :class="compact ? 'shrink-0 border-t' : 'mt-2 rounded-md border border-obsidian-200'"
    >
      <CommentAvatar :user-id="me?.id" :first-name="me?.firstName" :last-name="me?.lastName" />
      <CommentEditor
        ref="newThreadEditor"
        bare
        class="min-w-0 flex-1"
        :entity-type="entityType"
        :entity-id="entityId"
        :placeholder="t('placeholder')"
        :no-matches="t('noMatches')"
        :show-submit="true"
        :is-submitting="isCreating"
        :submit-label="t('comment')"
        @submit="handleSubmit(null)"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/vue';
import {
  CheckIcon,
  CircleCheckIcon,
  MessageSquareTextIcon,
  ReplyIcon,
  RotateCcwIcon,
} from 'lucide-vue-next';
import AnimatedHeightTransition from '@/components/AnimatedHeightTransition.vue';
import Spinner from '@/components/icons/Spinner.vue';
import CommentAvatar from './CommentAvatar.vue';
import CommentEditor from './CommentEditor.vue';
import CommentItem from './CommentItem.vue';
import { Alert } from '@/services/alert.service';
import { useMe } from '@/queries/use-users';
import {
  useComments,
  useCreateComment,
  useDeleteComment,
  useResolveComment,
  useToggleCommentReaction,
  useUpdateComment,
} from '@/queries/use-comments';
import type { CommentableEntityType, CommentThread, TipTapNode } from '@/api/comments.service';

const props = defineProps<{
  compact?: boolean;
  entity?: { id: string; booking?: { id: string } | null } | null;
  entityId: string;
  entityType: CommentableEntityType;
  hideTitle?: boolean;
}>();

const emit = defineEmits<{ 'threads-change': [] }>();

/** Per-thread height animation; the popover shell uses a shorter one so it can follow along. */
const ANIMATION_MS = 200;

const { t } = useI18n();
const ability = useAbility();
const { data: me } = useMe();
const showResolved = ref(false);
const replyingToId = ref<string | null>(null);
type CommentEditorExpose = {
  clear: () => void;
  getJSON: () => TipTapNode;
  isEmpty: () => boolean;
  setContent: (value: TipTapNode) => void;
};

const newThreadEditor = ref<CommentEditorExpose | null>(null);
// Refs declared inside a v-for are collected into an array by Vue; only one reply is ever open.
const replyEditor = ref<CommentEditorExpose[]>([]);
const target = computed(() => ({
  entityType: props.entityType,
  entityId: props.entityId,
}));

const canComment = computed(() => {
  if (props.entity) {
    return ability.can('comment', subject(props.entityType, props.entity));
  }
  return ability.can('comment', props.entityType);
});

const { data: threads, isError, isFetching } = useComments(target, () => canComment.value);
const { mutateAsync: createComment, isPending: isCreating } = useCreateComment(target);
const { mutateAsync: updateComment } = useUpdateComment(target);
const { mutateAsync: resolveComment } = useResolveComment(target);
const { mutateAsync: deleteComment } = useDeleteComment(target);
const { mutateAsync: toggleReaction } = useToggleCommentReaction(target);

const openCount = computed(
  () => (threads.value ?? []).filter((thread) => !thread.resolvedAt).length,
);
const resolvedCount = computed(() => (threads.value ?? []).length - openCount.value);

const visibleThreads = computed(() => {
  const items = threads.value ?? [];
  if (showResolved.value) return items;
  return items.filter((thread) => !thread.resolvedAt);
});

// Threads animate their own height, but appearing or disappearing is a jump only a
// surrounding container can smooth out.
watch(
  () => visibleThreads.value.length,
  () => emit('threads-change'),
);

const resolvedLabel = (thread: CommentThread) =>
  thread.resolvedBy
    ? t('resolvedBy', { name: `${thread.resolvedBy.firstName} ${thread.resolvedBy.lastName}` })
    : t('resolved');

const handleSubmit = async (parentId: string | null) => {
  const editor = parentId ? replyEditor.value[0] : newThreadEditor.value;
  if (!editor || editor.isEmpty()) return;
  const body = editor.getJSON();
  editor.clear();
  try {
    await createComment({ body, parentId });
    if (parentId) {
      replyingToId.value = null;
    }
  } catch {
    editor.setContent(body);
  }
};

const handleEdit = async ({ commentId, body }: { commentId: string; body: TipTapNode }) => {
  await updateComment({ commentId, body });
};

const handleDelete = async (commentId: string) => {
  const thread = (threads.value ?? []).find(
    (item) => item.id === commentId || item.replies.some((reply) => reply.id === commentId),
  );
  const isThreadRoot = thread?.id === commentId;
  const { isConfirmed } = await Alert.fire({
    titleText:
      isThreadRoot && (thread?.replies.length ?? 0) > 0
        ? t('confirmDeleteThread')
        : t('confirmDelete'),
    icon: 'warning',
    buttons: [
      { text: t('delete'), style: 'danger', propertyName: 'isConfirmed' },
      { text: t('cancel'), style: 'ghost', propertyName: 'isDismissed' },
    ],
  });
  if (!isConfirmed) return;
  await deleteComment(commentId);
};

const handleResolve = async (thread: CommentThread) => {
  await resolveComment({ commentId: thread.id, resolved: !thread.resolvedAt });
};

const handleReact = async ({ commentId, emoji }: { commentId: string; emoji: string }) => {
  await toggleReaction({ commentId, emoji });
};
</script>

<style scoped>
/* Threads posted while the panel is open fade in; the initial list renders without appear. */
.thread-enter-active {
  transition:
    opacity 200ms ease,
    transform 200ms ease;
}

.thread-enter-from {
  opacity: 0;
  transform: translateY(-0.25rem);
}

.loading-sweep {
  animation: loading-sweep 1s ease-in-out infinite;
}

@keyframes loading-sweep {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(400%);
  }
}
</style>

<i18n lang="json">
{
  "en": {
    "title": "Comments",
    "showResolved": "Show resolved ({count})",
    "hideResolved": "Hide resolved",
    "resolved": "Resolved",
    "resolvedBy": "Resolved by {name}",
    "empty": "No comments yet.",
    "loadError": "Could not load comments.",
    "justNow": "just now",
    "placeholder": "Add a comment",
    "replyPlaceholder": "Reply…",
    "noMatches": "No people found",
    "comment": "Comment",
    "reply": "Reply",
    "resolve": "Resolve",
    "unresolve": "Reopen",
    "save": "Save",
    "cancel": "Cancel",
    "edit": "Edit",
    "delete": "Delete",
    "confirmDelete": "Delete this comment?",
    "confirmDeleteThread": "Delete this thread and all replies?",
    "edited": "edited"
  },
  "de": {
    "title": "Kommentare",
    "showResolved": "Erledigte anzeigen ({count})",
    "hideResolved": "Erledigte ausblenden",
    "resolved": "Erledigt",
    "resolvedBy": "Erledigt von {name}",
    "empty": "Noch keine Kommentare.",
    "loadError": "Kommentare konnten nicht geladen werden.",
    "justNow": "gerade eben",
    "placeholder": "Kommentar hinzufügen",
    "replyPlaceholder": "Antworten…",
    "noMatches": "Keine Personen gefunden",
    "comment": "Kommentieren",
    "reply": "Antworten",
    "resolve": "Erledigen",
    "unresolve": "Wieder öffnen",
    "save": "Speichern",
    "cancel": "Abbrechen",
    "edit": "Bearbeiten",
    "delete": "Löschen",
    "confirmDelete": "Diesen Kommentar löschen?",
    "confirmDeleteThread": "Diesen Thread und alle Antworten löschen?",
    "edited": "bearbeitet"
  },
  "es": {
    "title": "Comentarios",
    "showResolved": "Mostrar resueltos ({count})",
    "hideResolved": "Ocultar resueltos",
    "resolved": "Resuelto",
    "resolvedBy": "Resuelto por {name}",
    "empty": "Aún no hay comentarios.",
    "loadError": "No se pudieron cargar los comentarios.",
    "justNow": "ahora mismo",
    "placeholder": "Añadir un comentario",
    "replyPlaceholder": "Responder…",
    "noMatches": "No se encontraron personas",
    "comment": "Comentar",
    "reply": "Responder",
    "resolve": "Resolver",
    "unresolve": "Reabrir",
    "save": "Guardar",
    "cancel": "Cancelar",
    "edit": "Editar",
    "delete": "Eliminar",
    "confirmDelete": "¿Eliminar este comentario?",
    "confirmDeleteThread": "¿Eliminar este hilo y todas las respuestas?",
    "edited": "editado"
  }
}
</i18n>
